import { useEffect, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { API_BASE } from '@/lib/api';

/**
 * Search-and-bind a real NFL roster row.
 *
 * A player prop settles against PlayerGameStat keyed by playerId, so the id is
 * bound here at creation rather than matched from a typed name at settlement.
 * A typed name leaves settlement guessing between two players who share one,
 * or holding forever — neither is a thing to discover after users have paid
 * coins for the prop.
 *
 * The list is filtered to the positions that can carry the chosen stat, so a
 * passing-yards prop cannot be pinned to a receiver.
 */

export interface PickedPlayer {
  id: string;
  name: string;
  team: string;
  position: string;
}

interface ApiPlayer {
  id: string;
  name: string;
  team: string | null;
  position: string | null;
}

export interface NflPlayerPickerProps {
  /** Bound player id, or '' for none. */
  value: string;
  /** Name to show for an already-bound player. */
  displayName: string;
  /** Positions eligible for the chosen stat; undefined means no filter. */
  positions?: readonly string[];
  /** Position of the already-bound player, when editing an existing prop. */
  boundPosition?: string;
  /** For the empty-state hint, e.g. "Passing yards". */
  statLabel?: string;
  onChange: (player: PickedPlayer | null) => void;
}

export function NflPlayerPicker({
  value,
  displayName,
  positions,
  boundPosition,
  statLabel,
  onChange,
}: NflPlayerPickerProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ApiPlayer[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  const positionKey = positions ? [...positions].join(',') : '';

  // A stat change can narrow the eligible positions out from under a bound
  // player — a receiver is fine for receiving yards and wrong the moment the
  // stat becomes passing yards. Release the binding rather than submitting a
  // prop that settles against a column that player never fills.
  //
  // The bound player's position is tracked here rather than looked up in
  // `results`: that list is cleared on pick, so a search-results lookup finds
  // nothing exactly when this needs to fire.
  useEffect(() => {
    if (!value || !boundPosition || !positions) return;
    if (!positions.includes(boundPosition)) onChange(null);
    // Only re-check when the eligible set changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionKey, value, boundPosition]);

  // Debounced search. An empty query still lists players so the field is
  // usable without typing.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    // Both flags move inside the debounce rather than firing synchronously:
    // a setState in the effect body cascades a render on every keystroke.
    const t = setTimeout(async () => {
      if (cancelled) return;
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ sport: 'NFL', limit: '25', active: 'true' });
        if (query.trim()) params.set('search', query.trim());
        if (positions?.length) params.set('position', positions.join(','));
        const res = await fetch(`${API_BASE}/api/players?${params}`);
        if (!res.ok) throw new Error(`search failed (${res.status})`);
        const body = await res.json();
        if (cancelled) return;
        setResults((body?.data?.players ?? body?.players ?? []) as ApiPlayer[]);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Search failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, positionKey, open, positions]);

  // Close on outside click so the list doesn't sit over the rest of the form.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const eligibility = positions?.length
    ? `${positions.join(' / ')}${statLabel ? ` — the positions that record ${statLabel.toLowerCase()}` : ''}`
    : null;

  return (
    <div ref={boxRef} className="relative">
      <label className="block text-xs text-zinc-400 mb-1">Player *</label>

      {value ? (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-950 border border-emerald-600/40">
          <span className="text-sm text-white font-medium">{displayName}</span>
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setQuery('');
              setOpen(true);
            }}
            className="ml-auto text-zinc-500 hover:text-zinc-300"
            aria-label="Clear player"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder="Search the roster…"
          className="w-full px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
        />
      )}

      {eligibility && !value && (
        <div className="mt-1 text-[10px] text-zinc-500 leading-tight">{eligibility}</div>
      )}

      {open && !value && (
        <div className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-zinc-700 bg-zinc-950 shadow-xl">
          {loading && (
            <div className="flex items-center gap-2 px-3 py-2 text-xs text-zinc-500">
              <Loader2 className="h-3 w-3 animate-spin" /> Searching…
            </div>
          )}
          {!loading && error && <div className="px-3 py-2 text-xs text-red-400">{error}</div>}
          {!loading && !error && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-zinc-500">
              No eligible players{query.trim() ? ` for “${query.trim()}”` : ''}.
            </div>
          )}
          {!loading &&
            results.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  onChange({
                    id: p.id,
                    name: p.name,
                    team: p.team ?? '',
                    position: p.position ?? '',
                  });
                  setOpen(false);
                  setQuery('');
                }}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-2 text-left text-sm',
                  'text-white hover:bg-zinc-800/80'
                )}
              >
                <span className="truncate">{p.name}</span>
                <span className="ml-auto shrink-0 text-[10px] text-zinc-500">
                  {[p.position, p.team].filter(Boolean).join(' · ')}
                </span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
